/**
 * @description Форматирует числовые значения для отображения в шапке сайта по правилам:
 * - Максимум 6 символов на одну валюту (включая запятые и точки)
 * - Тысячи разделяются запятой
 * - Десятичные значения допускаются
 * - При превышении лимита длины используются сокращения K / M / B
 *
 * @example
 * formatNumberShort(301000) -> "301K"
 * formatNumberShort(12_345) -> "12,345"
 * formatNumberShort(9.123456) -> "9.1234"
 * formatNumberShort(1_234_567) -> "1.23M"
 *
 * @param {number} value - исходное значение
 * @returns {string}
 */
export const formatNumberShort = (
  value: number,
  maxLen: number = 6,
  round: boolean = true,
  roundingMode: 'floor' | undefined = undefined,
  hideZero: boolean = true,
): string => {
  const MAX_LEN = maxLen;

  const normalizeNumericString = (s: string): string => {
    // Убираем лишние нули после запятой и висящую точку
    if (!s.includes('.')) return s;
    const trimmed = s.replace(/\.?0+$/, '');

    return hideZero ? (trimmed.length === 0 ? '0' : trimmed) : s;
  };

  const fits = (s: string): boolean => s.length <= MAX_LEN;

  const formatWithGrouping = (n: number, fractionDigits: number): string => {
    const res = n.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
      roundingMode: roundingMode,
    } as Intl.NumberFormatOptions);
    return res;
  };

  const tryPlain = (): string | null => {
    // Для всех значений допускаем десятичные по общему правилу
    if (Math.abs(value) < 10) {
      // X.XXXX → до 4 знаков после точки
      for (let fd = 4; fd >= 0; fd--) {
        const s = normalizeNumericString(formatWithGrouping(value, fd));
        if (fits(s)) return s;
      }
    } else if (Math.abs(value) < 1000) {
      // XX.XX → до 2 знаков
      for (let fd = 2; fd >= 0; fd--) {
        const s = normalizeNumericString(formatWithGrouping(value, fd));
        if (fits(s)) return s;
      }
    } else {
      // >= 1000, пробуем с разделителями тысяч без сокращений
      const s0 = formatWithGrouping(value, 0);
      if (fits(s0)) return s0;
    }
    return null;
  };

  const abbreviate = (): string => {
    const abs = Math.abs(value);
    const units: {suffix: 'K' | 'M' | 'B'; value: number}[] = [
      {suffix: 'B', value: 1_000_000_000},
      {suffix: 'M', value: 1_000_000},
      {suffix: 'K', value: 1_000},
    ];

    // Определяем подходящую единицу
    let chosen = null as null | {suffix: 'K' | 'M' | 'B'; value: number};
    for (const u of units) {
      if (abs >= u.value) {
        chosen = u;
        break;
      }
    }

    // Если меньше 1000 — не должно сюда попадать, но на всякий случай
    if (!chosen) {
      // Влезть в 6 символов обычным способом уже не удалось — вернем округленное
      return round ? Math.round(value).toString() : value.toString();
    }

    const base = value / chosen.value;
    const suffix = chosen.suffix;

    // Сколько символов осталось под число (с учетом суффикса)
    const availableForNumber = MAX_LEN - suffix.length;

    // Для сокращений допускаем дробные для всех — подбираем количество знаков после точки, чтобы уложиться
    // Оставляем место под точку, если будут дробные
    const maxFractionThatFits = (intLen: number): number => {
      const spaceLeft = availableForNumber - intLen - 1; // минус точка
      return Math.max(0, spaceLeft);
    };

    const intPart = Math.trunc(base);
    const intStr = intPart.toString();

    // Если даже целая часть не помещается — аналогично не токенам
    if (intStr.length > availableForNumber) {
      const rounded = round ? Math.round(base) : base;
      const roundedStr = rounded.toString();
      if (roundedStr.length <= availableForNumber) {
        return `${roundedStr}${suffix}`;
      }
      return `${roundedStr.slice(0, availableForNumber)}${suffix}`;
    }

    // Для K/M/B — не более 2 знаков после запятой. Предпочитаем максимум точности, который помещается.
    const maxAllowedFraction = Math.min(2, maxFractionThatFits(intStr.length));
    for (let fd = maxAllowedFraction; fd >= 1; fd--) {
      const s = base.toFixed(fd).replace(/\.?0+$/, '');
      if (s.length <= availableForNumber) {
        return `${s}${suffix}`;
      }
    }

    // Иначе только целая часть
    return `${intStr}${suffix}`;
  };

  const plain = tryPlain();
  if (plain) return plain;
  return abbreviate();
};
